/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import Helpers from 'application/Shared/Helpers/Functions';

/* Images */
import { ReactComponent as GoogleAdminLogo } from 'images/Google_Admin.svg';

/*  React  */
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const definitions = {
    ADD_LINK: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="ADD_LINK"
                    variant="primary"
                    src="AddCircle"
                    size={size === 'small' ? FontSizes[2] : FontSizes[3]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.application_add')}
            </span>
        )
    },
    AZURE_SIGN_IN: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="AZURE_SIGN_IN"
                    variant="primary"
                    src="Windows"
                    size={size === 'small' ? FontSizes[1] : FontSizes[2]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.microsoftEntraID.login')}
            </span>
        ),
        subTextFn: ({ metadata }) =>
            // renderLoginSubtext
            {
                // TODO: Decide what to do here re: the Accounts column and the security log object for
                // this activity not having a loginUsername set by the normalize transformer.
                // if (renderLoginSubtext && row.userPrincipalName) {
                if (metadata.userPrincipalName) {
                    return metadata.userPrincipalName;
                }
                return '';
            }
    },
    BROWSER_LOGIN: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="BROWSER_LOGIN"
                    variant="primary"
                    src="OpenInBrowser"
                    size={size === 'small' ? FontSizes[2] : FontSizes[3]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.browser_login')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            if (renderLoginSubtext && metadata.username) {
                return metadata.username;
            }

            return '';
        }
    },
    DOWNLOAD: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="download"
                    variant="primary"
                    src="CloudDownload"
                    size={size === 'small' ? FontSizes[2] : FontSizes[3]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.download')}
            </span>
        ),
        subTextFn: ({ metadata }) => {
            if (metadata.mime) {
                return (
                    <>
                        {metadata.mime}{' '}
                        {metadata.totalBytes
                            ? `(${Helpers.byteSizeFormatter(
                                  metadata.totalBytes
                              )})`
                            : ''}
                    </>
                );
            }
            return '';
        }
    },
    GOOGLE_SIGN_IN: {
        renderIcon: ({ theme }) => (
            <Box
                alt="Google Logo"
                sx={{
                    color: theme?.palette.icon.primary,
                    marginX: '8px',
                    width: '14px'
                }}
            >
                <GoogleAdminLogo />
            </Box>
        ),
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.google_login')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            if (renderLoginSubtext && metadata.username) {
                return metadata.username;
            }
            return '';
        }
    },
    GOOGLE_SSO: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="GOOGLE_SSO"
                    variant="primary"
                    src="Google"
                    size={size === 'small' ? FontSizes[1] : FontSizes[2]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.sso_google')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            const { username } = metadata;
            if (renderLoginSubtext && username) {
                return username;
            }

            return '';
        }
    },
    LOGIN: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="webLogin"
                    variant="primary"
                    src="VpnLock"
                    size={size === 'small' ? FontSizes[2] : FontSizes[3]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.form_login')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            if (renderLoginSubtext && metadata.email) {
                return metadata.email;
            }

            if (renderLoginSubtext && metadata.username) {
                return metadata.username;
            }

            return '';
        }
    },
    MICROSOFT_SSO: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="MICROSOFT_SSO"
                    variant="primary"
                    src="Microsoft"
                    size={size === 'small' ? FontSizes[1] : FontSizes[2]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Activity.sso_microsoft')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            const { username } = metadata;
            if (renderLoginSubtext && username) {
                return username;
            }

            return '';
        }
    },
    OKTA_SIGN_IN: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="OKTA_SIGN_IN"
                    variant="primary"
                    src="Okta"
                    size={size === 'small' ? FontSizes[1] : FontSizes[2]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Common.oktaLogin')}
            </span>
        )
    },
    QUICK_SCAN_LOGIN: {
        renderIcon: ({ params }) => {
            const { size } = params;

            return (
                <IconWithVariant
                    key="quickScan"
                    variant="primary"
                    src="Clock"
                    size={size === 'small' ? FontSizes[2] : FontSizes[3]}
                    wrapperProps={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '30px'
                    }}
                />
            );
        },
        renderName: ({ t, theme }) => (
            <span style={{ color: theme.palette.text.secondary }}>
                {t('Common.browser_history')}
            </span>
        ),
        subTextFn: ({ metadata, renderLoginSubtext }) => {
            if (renderLoginSubtext && metadata.username) {
                return metadata.username;
            }

            return '';
        }
    }
};

const ActivityContext = React.createContext({
    activity: null,
    id: null,
    metadata: null,
    renderLoginSubtext: true
});

const Activity = ({
    children,
    activity,
    metadata,
    id,
    renderLoginSubtext = true
}) => {
    const activityMemo = useMemo(
        () => ({ activity, id, metadata, renderLoginSubtext }),
        [activity, metadata, id, renderLoginSubtext]
    );

    return (
        <ActivityContext.Provider value={activityMemo}>
            {children}
        </ActivityContext.Provider>
    );
};

const ActivityIconAndName = ({ subText }) => {
    const { activity } = useContext(ActivityContext);

    if (!activity) {
        return null;
    }

    return (
        <Box display="flex" alignItems="flex-start">
            {/* 30px to match the icons width above */}
            <Box width="30px">
                <ActivityIcon />
            </Box>
            <ActivityName subText={subText} />
        </Box>
    );
};

const ActivityIcon = ({ size }) => {
    const theme = useTheme();
    const { activity } = useContext(ActivityContext);

    if (!Helpers.isset(definitions, activity)) {
        return null;
    }

    const params = { size: 'medium' };
    if (size) {
        params.size = size;
    }

    return definitions[activity].renderIcon({ params, theme });
};

const ActivityName = ({ subText }) => {
    const theme = useTheme();
    const { activity, metadata, renderLoginSubtext } =
        useContext(ActivityContext);
    const { t } = useTranslation();

    if (!Helpers.isset(definitions, activity)) {
        return <Typography color="text.disabled">{activity}</Typography>;
    }

    const opts = definitions[activity];
    // eslint-disable-next-line testing-library/render-result-naming-convention
    const activityText = opts.renderName({ t, theme });

    return (
        <Box display="flex" flexDirection="column" paddingLeft="2px">
            {activityText}
            {!!subText && !!opts.subTextFn && (
                <small>
                    {opts.subTextFn({ metadata, renderLoginSubtext })}
                </small>
            )}
        </Box>
    );
};

const ActivityIdAndIPAddress = ({ ip }) => {
    const { id } = useContext(ActivityContext);
    const { t } = useTranslation();

    return (
        <>
            {ip && <Box>{t('Activity.ip', { ip })}</Box>}
            {id && <Box>{t('Activity.id', { id })}</Box>}
        </>
    );
};

export {
    Activity,
    ActivityName,
    ActivityIconAndName,
    ActivityIcon,
    ActivityIdAndIPAddress
};
